import { z } from 'zod';
import { INDUSTRIES, PLAN_TIERS, SUBSCRIPTION_STATUS } from '../enums.js';
import { EntityMetaSchema, zEmail, zEnum, zNonEmpty, zPhone } from './common.js';

/**
 * PLATFORM lead = a business interested in buying Worksy360. Completely separate from tenant leads.
 * Captured from the marketing site; visible only to platform admins.
 */
export const PlatformLeadSchema = EntityMetaSchema.extend({
  name: zNonEmpty.max(120),
  businessName: z.string().max(160).optional(),
  email: zEmail,
  phone: zPhone.optional(),
  trade: zEnum(INDUSTRIES).optional(),
  employeeCount: z.string().max(20).optional(), // "1-5", "6-20", etc.
  message: z.string().max(4000).optional(),
  status: z.enum(['new', 'contacted', 'trialing', 'converted', 'lost']).default('new'),
  // Attribution
  source: z.string().max(120).optional(),
  utmSource: z.string().max(120).optional(),
  utmMedium: z.string().max(120).optional(),
  utmCampaign: z.string().max(120).optional(),
  landingPage: z.string().max(200).optional(),
});
export type PlatformLead = z.infer<typeof PlatformLeadSchema>;

/** Public marketing lead-capture form input. Kept intentionally small to maximize conversion. */
export const CapturePlatformLeadSchema = z.object({
  name: zNonEmpty.max(120),
  businessName: z.string().max(160).optional(),
  email: zEmail,
  phone: zPhone.optional(),
  trade: zEnum(INDUSTRIES).optional(),
  employeeCount: z.string().max(20).optional(),
  message: z.string().max(4000).optional(),
  source: z.string().max(120).optional(),
  utmSource: z.string().max(120).optional(),
  utmMedium: z.string().max(120).optional(),
  utmCampaign: z.string().max(120).optional(),
  landingPage: z.string().max(200).optional(),
  // Honeypot: must be empty (bots fill it). Not persisted.
  website: z.string().max(0).optional(),
});
export type CapturePlatformLeadInput = z.infer<typeof CapturePlatformLeadSchema>;

/** SaaS subscription for a tenant (Stripe Billing). Separate from tenant->customer payments. */
export const SubscriptionSchema = EntityMetaSchema.extend({
  tier: zEnum(PLAN_TIERS).default('solo'),
  status: zEnum(SUBSCRIPTION_STATUS).default('trialing'),
  seats: z.number().int().min(1).default(1),
  interval: z.enum(['monthly', 'annual']).default('monthly'),
  trialEndsAt: z.string().optional(),
  currentPeriodEndsAt: z.string().optional(),
  stripeCustomerId: z.string().optional(),
  stripeSubscriptionId: z.string().optional(),
  canceledAt: z.string().optional(),
});
export type Subscription = z.infer<typeof SubscriptionSchema>;
